import { memo, type ReactNode } from 'react';
import { Button } from './surfacePrimitives.js';
import './StickySaveBar.css';

interface StickySaveBarChromeProps {
  readonly className?: string;
  /** When true, adds footer flex push (`margin-top: auto`) for card footers. */
  readonly stickToFooter?: boolean;
  /**
   * Modal / DialogActions host already owns chrome + BN clearance.
   * Flattens StickySaveBar to the four-part hint+actions row (no second sticky offset).
   */
  readonly embedded?: boolean;
  /** Optional id on the hint `<p>` for `aria-describedby` from CTA buttons. Default: no id. */
  readonly hintId?: string;
}

interface StickySaveBarBaseProps extends StickySaveBarChromeProps {
  readonly hint: string;
  readonly label: string;
  readonly busy: boolean;
  readonly onSave: () => void;
  readonly disabled?: boolean;
}

type StickySaveBarWithCancelProps = StickySaveBarBaseProps & {
  readonly cancelLabel: string;
  readonly onCancel: () => void;
  readonly children?: undefined;
};

type StickySaveBarWithoutCancelProps = StickySaveBarBaseProps & {
  readonly cancelLabel?: undefined;
  readonly onCancel?: undefined;
  readonly children?: undefined;
};

/** Compose custom action nodes inside StickySaveBar chrome (EntityFormLayout). */
type StickySaveBarSlotProps = StickySaveBarChromeProps & {
  readonly hint?: string;
  readonly children: ReactNode;
  readonly label?: undefined;
  readonly busy?: undefined;
  readonly onSave?: undefined;
  readonly disabled?: undefined;
  readonly cancelLabel?: undefined;
  readonly onCancel?: undefined;
};

export type StickySaveBarProps =
  | StickySaveBarWithCancelProps
  | StickySaveBarWithoutCancelProps
  | StickySaveBarSlotProps;

function stickySaveBarRootClass(
  className: string,
  stickToFooter: boolean,
  embedded: boolean,
): string {
  return [
    'admin-sticky-save-bar',
    stickToFooter ? 'admin-sticky-save-bar--footer' : '',
    embedded ? 'admin-sticky-save-bar--embedded' : '',
    className,
  ]
    .filter((part) => part.length > 0)
    .join(' ');
}

/** Spec1-P12 StickySaveBar — four-part sticky CTA: chrome · hint · cancel · save (BN via `--admin-bottom-chrome`). */
export const StickySaveBar = memo((props: StickySaveBarProps): JSX.Element => {
  const { className = '', stickToFooter = false, embedded = false } = props;
  const rootClass = stickySaveBarRootClass(className, stickToFooter, embedded);
  const allowlist = embedded ? 'modal-footer' : 'form-action-bar';

  const hintId = props.hintId;

  if ('children' in props && props.children != null) {
    const hint = props.hint;
    const showHint =
      hintId != null || (hint != null && hint !== '');
    return (
      <div
        className={rootClass}
        data-testid="sticky-save-bar"
        data-sticky-allowlist={allowlist}
      >
        {showHint ? (
          <p id={hintId} className="admin-sticky-save-hint">
            {hint ?? ''}
          </p>
        ) : null}
        <div className="admin-sticky-save-actions">{props.children}</div>
      </div>
    );
  }

  const {
    hint,
    label,
    cancelLabel,
    busy,
    onSave,
    onCancel,
    disabled = false,
  } = props as StickySaveBarWithCancelProps | StickySaveBarWithoutCancelProps;

  return (
    <div
      className={rootClass}
      data-testid="sticky-save-bar"
      data-sticky-allowlist={allowlist}
    >
      {hintId != null || hint !== '' ? (
        <p id={hintId} className="admin-sticky-save-hint">
          {hint}
        </p>
      ) : null}
      <div className="admin-sticky-save-actions">
        {onCancel != null && cancelLabel != null && cancelLabel !== '' ? (
          <Button type="button" intent="secondary" disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </Button>
        ) : null}
        <Button type="button" intent="primary" disabled={busy || disabled} onClick={onSave}>
          {label}
        </Button>
      </div>
    </div>
  );
});

StickySaveBar.displayName = 'StickySaveBar';
